import {
  PackageIcon,
  CrownIcon,
  Database01Icon,
  FingerPrintIcon,
  FolderCodeIcon,
  Globe02Icon,
  HardDriveIcon,
  Key01Icon,
  SquareLock01Icon,
  Mail01Icon,
  HierarchyIcon,
  Radar01Icon,
  Ticket01Icon,
  type TerraIcon,
} from '@terra/ui-icons';
import { token } from '@terra/ui-styled-system';
import type {
  InternalNetworkAccessedResourceKind,
  InternalNetworkAdEntityKind,
  InternalNetworkAttackPathEventKind,
  InternalNetworkCredentialProvenance,
} from '@terra/shared-contracts-internal-network';
import type { InfraImpact } from '@terra/shared-schemas-orchestrator';
import {
  GRAPH_NODE_COLOR as EXTERNAL_GRAPH_NODE_COLOR,
  GRAPH_NODE_ICON as EXTERNAL_GRAPH_NODE_ICON,
  type GraphNodeType,
} from '../external-network/constants';
import {
  GRAPH_NODE_COLOR as AD_ENTITY_GRAPH_COLOR,
  GRAPH_NODE_ICON as AD_ENTITY_GRAPH_ICON,
} from './constants';
import type {
  AttackPathChainStatus,
  AttackPathEventNodeData,
  AttackPathEventNodeRole,
} from './attack-path-event-node.types';

export type AttackPathEventVisual = {
  icon: TerraIcon;
  color: string;
  legendLabelKey: string;
};

const AD_ENTITY_TO_EXTERNAL_KIND: Record<
  InternalNetworkAdEntityKind,
  GraphNodeType
> = {
  USER: 'SOFTWARE',
  GROUP: 'NETBLOCK',
  COMPUTER: 'HOST',
  OU: 'SOFTWARE',
  DOMAIN: 'DOMAIN',
  GPO: 'STORAGE_SERVICE',
  DOMAIN_CONTROLLER: 'GATEWAY',
  CERT_TEMPLATE: 'SOFTWARE',
  CONTAINER: 'CLOUD_ACCOUNT',
};

export const resolveExternalGraphColorForAdEntity = (
  entityKind: InternalNetworkAdEntityKind,
): string => EXTERNAL_GRAPH_NODE_COLOR[AD_ENTITY_TO_EXTERNAL_KIND[entityKind]];

export const resolveExternalGraphIconForAdEntity = (
  entityKind: InternalNetworkAdEntityKind,
): TerraIcon =>
  EXTERNAL_GRAPH_NODE_ICON[AD_ENTITY_TO_EXTERNAL_KIND[entityKind]];

export const ATTACK_PATH_EVENT_VISUALS: Record<
  InternalNetworkAttackPathEventKind,
  AttackPathEventVisual
> = {
  HOST_DISCOVERED: {
    icon: EXTERNAL_GRAPH_NODE_ICON.HOST,
    color: EXTERNAL_GRAPH_NODE_COLOR.HOST,
    legendLabelKey: 'attackPathGraph.legend.host',
  },
  SERVICE_DISCOVERED: {
    icon: EXTERNAL_GRAPH_NODE_ICON.NETWORK_SERVICE,
    color: EXTERNAL_GRAPH_NODE_COLOR.NETWORK_SERVICE,
    legendLabelKey: 'attackPathGraph.legend.service',
  },
  WEAKNESS_EXPLOITED: {
    icon: EXTERNAL_GRAPH_NODE_ICON.WEAKNESS,
    color: EXTERNAL_GRAPH_NODE_COLOR.HOST,
    legendLabelKey: 'attackPathGraph.legend.weakness',
  },
  CREDENTIAL_OBTAINED: {
    icon: EXTERNAL_GRAPH_NODE_ICON.SOFTWARE,
    color: EXTERNAL_GRAPH_NODE_COLOR.SOFTWARE,
    legendLabelKey: 'attackPathGraph.legend.credential',
  },
  DATA_ACCESSED: {
    icon: EXTERNAL_GRAPH_NODE_ICON.STORAGE_SERVICE,
    color: EXTERNAL_GRAPH_NODE_COLOR.STORAGE_SERVICE,
    legendLabelKey: 'attackPathGraph.legend.data',
  },
  IMPACT_ACHIEVED: {
    icon: EXTERNAL_GRAPH_NODE_ICON.GATEWAY,
    color: EXTERNAL_GRAPH_NODE_COLOR.GATEWAY,
    legendLabelKey: 'attackPathGraph.legend.impact',
  },
};
export const DOMAIN_CONTROLLER_ICON: TerraIcon =
  EXTERNAL_GRAPH_NODE_ICON.GATEWAY;
const CREDENTIAL_PROVENANCE_ICON: Record<
  InternalNetworkCredentialProvenance,
  TerraIcon
> = {
  DISCOVERED: Key01Icon,
  RELAYED: HierarchyIcon,
  CRACKED: FingerPrintIcon,
  COMPROMISED: SquareLock01Icon,
};
const RESOURCE_KIND_ICON: Record<
  InternalNetworkAccessedResourceKind,
  TerraIcon
> = {
  REPOSITORY: FolderCodeIcon,
  FILE_SHARE: HardDriveIcon,
  DATABASE: Database01Icon,
  OBJECT_STORAGE: PackageIcon,
  MAILBOX: Mail01Icon,
  BACKUP: Database01Icon,
};

export const resolveAttackPathEventIcon = ({
  kind,
  provenance,
  resourceKind,
  isDomainController,
  role,
  entityKind,
}: {
  kind: InternalNetworkAttackPathEventKind;
  provenance?: InternalNetworkCredentialProvenance | null;
  resourceKind?: InternalNetworkAccessedResourceKind | null;
  isDomainController?: boolean;
  role?: AttackPathEventNodeRole;
  entityKind?: InternalNetworkAdEntityKind | null;
}): TerraIcon => {
  if (
    (role === 'OWNED_SEED' ||
      role === 'CROWN_JEWEL_SEED' ||
      role === 'ENTITY') &&
    entityKind != null
  ) {
    return AD_ENTITY_GRAPH_ICON[entityKind];
  }

  if (role === 'CROWN_JEWEL_CLUSTER') {
    return CrownIcon;
  }

  if (kind === 'HOST_DISCOVERED' && isDomainController) {
    return DOMAIN_CONTROLLER_ICON;
  }

  if (kind === 'CREDENTIAL_OBTAINED' && provenance) {
    return CREDENTIAL_PROVENANCE_ICON[provenance];
  }

  if (kind === 'DATA_ACCESSED' && resourceKind) {
    return RESOURCE_KIND_ICON[resourceKind];
  }

  return ATTACK_PATH_EVENT_VISUALS[kind].icon;
};

export const translucentColor = ({
  color,
  percent,
}: {
  color: string;
  percent: number;
}): string => `color-mix(in srgb, ${color} ${percent}%, transparent)`;

export const INFRA_IMPACT_COLOR: Record<InfraImpact, string> = {
  DOMAIN_COMPROMISE: EXTERNAL_GRAPH_NODE_COLOR.DOMAIN,
  HOST_COMPROMISE: EXTERNAL_GRAPH_NODE_COLOR.HOST,
  DOMAIN_USER_COMPROMISE: EXTERNAL_GRAPH_NODE_COLOR.DOMAIN,
  SENSITIVE_DATA_EXPOSURE: EXTERNAL_GRAPH_NODE_COLOR.STORAGE_SERVICE,
  RANSOMWARE_EXPOSURE: EXTERNAL_GRAPH_NODE_COLOR.GATEWAY,
  CREDENTIAL_EXPOSURE: EXTERNAL_GRAPH_NODE_COLOR.SOFTWARE,
  PRIVILEGE_ESCALATION: EXTERNAL_GRAPH_NODE_COLOR.SOFTWARE,
  LATERAL_MOVEMENT: EXTERNAL_GRAPH_NODE_COLOR.NETWORK_SERVICE,
};
export const ATTACK_PATH_GRAPH_EDGE_COLOR = EXTERNAL_GRAPH_NODE_COLOR.HOST;
export const ATTACK_PATH_CROWN_JEWEL_COLOR = EXTERNAL_GRAPH_NODE_COLOR.DOMAIN;
export const ATTACK_PATH_FAILED_EVENT_COLOR = token('colors.red.9');

export const isFailedAttackPathEventStatus = (
  status?: string | null,
): boolean => status === 'FAILED';

export const isAbortedCampaignStatus = (status?: string | null): boolean =>
  status === 'ABORTED';

export type AttackPathCampaignOutcome = 'failed' | 'succeeded' | 'pending';

export const resolveAttackPathCampaignOutcome = (
  status?: string | null,
): AttackPathCampaignOutcome => {
  if (isAbortedCampaignStatus(status)) {
    return 'failed';
  }

  return status === 'COMPLETED' ? 'succeeded' : 'pending';
};

export type AttackPathEventNodeColorInput = Pick<
  AttackPathEventNodeData,
  'role' | 'kind' | 'status' | 'entityKind'
> &
  Partial<Pick<AttackPathEventNodeData, 'impacts'>>;

export const resolveAttackPathEventNodeColor = (
  data: AttackPathEventNodeColorInput,
): string => {
  if (data.role === 'UNKNOWN_GAP') {
    return token('colors.bg.muted');
  }

  if (data.role === 'CROWN_JEWEL_CLUSTER') {
    return ATTACK_PATH_CROWN_JEWEL_COLOR;
  }

  if (
    (data.role === 'OWNED_SEED' ||
      data.role === 'CROWN_JEWEL_SEED' ||
      data.role === 'ENTITY') &&
    data.entityKind != null
  ) {
    return AD_ENTITY_GRAPH_COLOR[data.entityKind];
  }

  if (data.role === 'CROWN_JEWEL_SEED') {
    return ATTACK_PATH_CROWN_JEWEL_COLOR;
  }

  const primaryImpact = data.impacts?.[0];

  if (data.kind === 'IMPACT_ACHIEVED' && primaryImpact != null) {
    return INFRA_IMPACT_COLOR[primaryImpact];
  }

  return ATTACK_PATH_EVENT_VISUALS[data.kind].color;
};

export const ATTACK_PATH_CHAIN_STATUS_OUTLINE_COLOR: Record<
  AttackPathChainStatus,
  string
> = {
  COMPLETED: EXTERNAL_GRAPH_NODE_COLOR.NETWORK_SERVICE,
  IN_PROGRESS: EXTERNAL_GRAPH_NODE_COLOR.SOFTWARE,
  BLOCKED: ATTACK_PATH_FAILED_EVENT_COLOR,
};

export const resolveAttackPathChainOutlineColor = (
  status: AttackPathChainStatus,
): string => ATTACK_PATH_CHAIN_STATUS_OUTLINE_COLOR[status];

export const resolveAttackPathEventNodeFrameColor = ({
  data,
  emphasized,
}: {
  data: AttackPathEventNodeColorInput &
    Pick<AttackPathEventNodeData, 'chainStatus'>;
  emphasized: boolean;
}): string => {
  if (emphasized) {
    return token('colors.border.accent.default');
  }

  if (data.role === 'EVENT') {
    return resolveAttackPathChainOutlineColor(data.chainStatus);
  }

  return translucentColor({
    color: resolveAttackPathEventNodeColor(data),
    percent: 45,
  });
};

export const ATTACK_PATH_ADDITIONAL_LEGEND_ICONS = {
  domainController: EXTERNAL_GRAPH_NODE_ICON.GATEWAY,
  crownJewel: CrownIcon,
  proven: Radar01Icon,
  ticket: Ticket01Icon,
  key: Key01Icon,
  globe: Globe02Icon,
} as const;
